// Free Music API Integration using Jamendo API
// Jamendo provides free music with Creative Commons licenses

export interface Track {
  id: string;
  name: string;
  artist: string;
  album: string;
  duration: number;
  audioUrl: string;
  imageUrl: string;
  genre: string;
}

export interface Artist {
  id: string;
  name: string;
  image: string;
  joindate: string;
}

export interface Album {
  id: string;
  name: string;
  artist_name: string;
  image: string;
  releasedate: string;
}

class MusicApiService {
  private baseUrl = 'https://api.jamendo.com/v3.0';
  private clientId = 'YOUR_CLIENT_ID'; // Users can get free API key from Jamendo

  // Get popular tracks
  async getPopularTracks(limit: number = 20): Promise<Track[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/tracks/?client_id=${this.clientId}&format=json&limit=${limit}&order=popularity_total&include=musicinfo&audioformat=mp32`
      );
      const data = await response.json();
      
      return data.results.map((track: any) => ({
        id: track.id,
        name: track.name,
        artist: track.artist_name,
        album: track.album_name,
        duration: track.duration,
        audioUrl: track.audio,
        imageUrl: track.album_image || track.image,
        genre: track.musicinfo?.tags?.genres?.[0] || 'Unknown'
      }));
    } catch (error) {
      console.error('Error fetching popular tracks:', error);
      return this.getFallbackTracks();
    }
  }

  // Search tracks
  async searchTracks(query: string, limit: number = 20): Promise<Track[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/tracks/?client_id=${this.clientId}&format=json&limit=${limit}&search=${encodeURIComponent(query)}&include=musicinfo&audioformat=mp32`
      );
      const data = await response.json();
      
      return data.results.map((track: any) => ({
        id: track.id,
        name: track.name,
        artist: track.artist_name,
        album: track.album_name,
        duration: track.duration,
        audioUrl: track.audio,
        imageUrl: track.album_image || track.image,
        genre: track.musicinfo?.tags?.genres?.[0] || 'Unknown'
      }));
    } catch (error) {
      console.error('Error searching tracks:', error);
      return [];
    }
  }

  // Get tracks by genre
  async getTracksByGenre(genre: string, limit: number = 20): Promise<Track[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/tracks/?client_id=${this.clientId}&format=json&limit=${limit}&tags=${genre}&include=musicinfo&audioformat=mp32`
      );
      const data = await response.json();
      
      return data.results.map((track: any) => ({
        id: track.id,
        name: track.name,
        artist: track.artist_name,
        album: track.album_name,
        duration: track.duration,
        audioUrl: track.audio,
        imageUrl: track.album_image || track.image,
        genre: track.musicinfo?.tags?.genres?.[0] || genre
      }));
    } catch (error) {
      console.error('Error fetching tracks by genre:', error);
      return [];
    }
  }

  // Get featured artists
  async getFeaturedArtists(limit: number = 10): Promise<Artist[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/artists/?client_id=${this.clientId}&format=json&limit=${limit}&order=popularity_total`
      );
      const data = await response.json();
      
      return data.results.map((artist: any) => ({
        id: artist.id,
        name: artist.name,
        image: artist.image,
        joindate: artist.joindate
      }));
    } catch (error) {
      console.error('Error fetching artists:', error);
      return [];
    }
  }

  // Get new releases
  async getNewReleases(limit: number = 20): Promise<Album[]> {
    try {
      const response = await fetch(
        `${this.baseUrl}/albums/?client_id=${this.clientId}&format=json&limit=${limit}&order=releasedate_desc`
      );
      const data = await response.json();
      
      return data.results.map((album: any) => ({
        id: album.id,
        name: album.name,
        artist_name: album.artist_name,
        image: album.image,
        releasedate: album.releasedate
      }));
    } catch (error) {
      console.error('Error fetching new releases:', error);
      return [];
    }
  }

  // Fallback tracks for demo purposes
  private getFallbackTracks(): Track[] {
    return [
      {
        id: 'demo1',
        name: 'Demo Track 1',
        artist: 'Demo Artist',
        album: 'Demo Album',
        duration: 180,
        audioUrl: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.wav',
        imageUrl: 'https://readdy.ai/api/search-image?query=music%20album%20cover%20with%20abstract%20geometric%20patterns%2C%20vibrant%20colors%2C%20modern%20design&width=300&height=300&seq=demo-cover-1&orientation=squarish',
        genre: 'Electronic'
      },
      {
        id: 'demo2',
        name: 'Demo Track 2',
        artist: 'Demo Artist 2',
        album: 'Demo Album 2',
        duration: 210,
        audioUrl: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.wav',
        imageUrl: 'https://readdy.ai/api/search-image?query=music%20album%20cover%20with%20flowing%20waves%2C%20purple%20and%20blue%20gradients%2C%20artistic%20design&width=300&height=300&seq=demo-cover-2&orientation=squarish',
        genre: 'Pop'
      }
    ];
  }

  // Format duration from seconds to mm:ss
  formatDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
}

export const musicApiService = new MusicApiService();